// pages/AuthPage.js

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PicProfileLogo     from "../components/AuthPage/LogoHeader";
import LoginForm          from "../components/AuthPage/LoginForm";
import SignupForm         from "../components/AuthPage/SignupForm";
import ForgotPasswordForm from "../components/AuthPage/ForgotPasswordForm";
import { useAuth }        from "../components/AuthPage/AuthContext";
import Ballpit            from "../components/Ballpit/Ballpit";

export default function AuthPage() {
  const [mode,         setMode]         = useState("login");
  const [key,          setKey]          = useState(0);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [legalModal,   setLegalModal]   = useState(null); // "terms" | "privacy" | null

  const navigate = useNavigate();
  const { login } = useAuth();

  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m);
    setKey((k) => k + 1);
    setPendingEmail(null);
  };

  const handleFormSuccess = async (data) => {
    if (data?.email && !data?.access) {
      setPendingEmail(data.email);
      return;
    }
    await login(null, data?.access);
    navigate("/home");
  };

  const handleVerified = () => {
    setPendingEmail(null);
    switchMode("login");
  };

  const isForgot = mode === "forgot";

  return (
    <div className="relative min-h-screen bg-[#040810] text-slate-200" style={{ overflowX: "hidden", overflowY: "auto" }}>

      {/* ── Ballpit: fixed, entire page ─────────────────────── */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0 }}>
        <Ballpit
          count={80}
          gravity={0.01}
          friction={0.9975}
          wallBounce={0.95}
          followCursor={true}
          colors={[0x0a0e1a, 0x3a1a2e, 0xF65CB1]}
        />
      </div>

      {/* ── Layout: card centred on all screens ─────────────── */}
      <div className="relative flex min-h-screen items-center justify-center" style={{ zIndex: 1 }}>

        <div className="w-full flex items-center justify-center px-4 py-8">
          <div
            className="w-full max-w-[420px] rounded-3xl overflow-hidden auth-card-enter"
            style={{
              background: "rgba(10, 14, 26, 0.42)",
              backdropFilter: "blur(22px)",
              WebkitBackdropFilter: "blur(22px)",
              border: "1px solid rgba(255,255,255,0.10)",
              boxShadow:
                "0 8px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(246,92,177,0.05) inset, 0 1px 0 rgba(255,255,255,0.06) inset",
            }}
          >
            {/* Top accent line */}
            <div style={{
              height: 1,
              background:
                "linear-gradient(90deg, transparent, rgba(246,92,177,0.5) 40%, rgba(246,92,154,0.25) 60%, transparent)",
            }} />

            <div className="px-5 sm:px-8 pt-6 sm:pt-8 pb-6 sm:pb-7">

              <PicProfileLogo />

              {/* ── Tabs ─────────────────────────── */}
              {!isForgot && (
                <div
                  className="flex gap-1 p-1 rounded-xl mb-6"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {["login", "signup"].map((m) => (
                    <button
                      key={m}
                      onClick={() => switchMode(m)}
                      className={[
                        "flex-1 py-[10px] rounded-[9px] text-[12px] font-semibold tracking-[.08em] uppercase transition-all duration-300",
                        mode === m
                          ? "text-white"
                          : "bg-transparent text-slate-500/40 hover:text-slate-400/70",
                      ].join(" ")}
                      style={mode === m ? {
                        background: "linear-gradient(135deg, rgba(246,92,154,0.25), rgba(246,92,177,0.15))",
                        border: "1px solid rgba(246,92,177,0.35)",
                        boxShadow: "0 0 18px rgba(246,92,177,0.15)",
                      } : {}}
                    >
                      {m === "login" ? "Sign In" : "Sign Up"}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Forgot header ─────────────────── */}
              {isForgot && (
                <div className="flex items-center gap-2 mb-6">
                  <button
                    onClick={() => switchMode("login")}
                    className="text-slate-500/60 hover:text-slate-300 transition-colors"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 12H5M12 5l-7 7 7 7" />
                    </svg>
                  </button>
                  <span className="text-[13px] text-slate-400">Reset Password</span>
                </div>
              )}

              {/* ── Form ──────────────────────────── */}
              <div key={key}>
                {isForgot ? (
                  <ForgotPasswordForm onGoToLogin={() => switchMode("login")} />
                ) : mode === "login" ? (
                  <LoginForm
                    onSuccess={handleFormSuccess}
                    onForgotPassword={() => switchMode("forgot")}
                  />
                ) : (
                  <SignupForm
                    onSuccess={handleFormSuccess}
                    pendingEmail={pendingEmail}
                    onVerified={handleVerified}
                  />
                )}
              </div>

              {/* ── Terms ─────────────────────────── */}
              <p className="mt-6 text-center text-[11px] text-slate-600/70 leading-relaxed">
                By continuing, you agree to our{" "}
                <button type="button" onClick={() => setLegalModal("terms")} className="text-slate-500 hover:text-blue-400 transition-colors underline underline-offset-2">
                  Terms of Service
                </button>{" "}
                and{" "}
                <button type="button" onClick={() => setLegalModal("privacy")} className="text-slate-500 hover:text-blue-400 transition-colors underline underline-offset-2">
                  Privacy Policy
                </button>
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* ── Legal Modal ─────────────────────────────────────── */}
      {legalModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ backgroundColor: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
          onClick={() => setLegalModal(null)}
        >
          <div
            className="w-full max-w-[480px] rounded-2xl overflow-hidden"
            style={{
              background: "rgba(10,14,26,0.95)",
              border: "1px solid rgba(255,255,255,0.10)",
              boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
              maxHeight: "80vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-sm">
                {legalModal === "terms" ? "Terms of Service" : "Privacy Policy"}
              </h2>
              <button
                onClick={() => setLegalModal(null)}
                className="text-slate-500 hover:text-white transition-colors text-lg leading-none"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5 overflow-y-auto text-slate-400 text-[13px] leading-relaxed" style={{ maxHeight: "calc(80vh - 60px)" }}>
              {legalModal === "terms" ? (
                <>
                  <p className="mb-4">By using PicProfile, you agree to these terms. Please read them carefully.</p>
                  <p className="font-semibold text-white mb-1">1. Use of Service</p>
                  <p className="mb-4">You must be at least 13 years old to use PicProfile. You are responsible for your account and all activity that occurs under it.</p>
                  <p className="font-semibold text-white mb-1">2. Content</p>
                  <p className="mb-4">You retain ownership of content you post. By posting, you grant PicProfile a non-exclusive license to display that content on the platform.</p>
                  <p className="font-semibold text-white mb-1">3. Prohibited Conduct</p>
                  <p className="mb-4">You may not post content that is illegal, harmful, or violates the rights of others. We reserve the right to remove content and suspend accounts that violate these terms.</p>
                  <p className="font-semibold text-white mb-1">4. Changes</p>
                  <p>We may update these terms from time to time. Continued use of PicProfile after changes constitutes acceptance of the new terms.</p>
                </>
              ) : (
                <>
                  <p className="mb-4">Your privacy matters to us. This policy explains what data we collect and how we use it.</p>
                  <p className="font-semibold text-white mb-1">1. Data We Collect</p>
                  <p className="mb-4">We collect information you provide (name, email, profile picture) and usage data (posts viewed, interactions) to personalise your experience.</p>
                  <p className="font-semibold text-white mb-1">2. How We Use It</p>
                  <p className="mb-4">We use your data to operate the platform, improve our services, and show relevant content. We do not sell your personal data to third parties.</p>
                  <p className="font-semibold text-white mb-1">3. Cookies</p>
                  <p className="mb-4">We use cookies and local storage to keep you logged in and remember your preferences.</p>
                  <p className="font-semibold text-white mb-1">4. Your Rights</p>
                  <p>You can request deletion of your account and data at any time by contacting us.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
